import { useState, useCallback, useEffect, useRef } from 'react';
import { checkSlugAvailability } from '@paulownia/shared/services/tenant';

/**
 * Slug validation status
 */
export type SlugStatus = 'idle' | 'checking' | 'available' | 'taken' | 'invalid';

/**
 * Slug validation result with detailed reason
 */
export interface SlugValidationResult {
  status: SlugStatus;
  message?: string;
}

/**
 * Options for slug validation
 */
export interface UseSlugValidationOptions {
  /** Minimum slug length (default: 3) */
  minLength?: number;
  /** Debounce delay in milliseconds (default: 500) */
  debounceMs?: number;
  /** Callback when slug status changes */
  onStatusChange?: (status: SlugStatus) => void;
}

/**
 * Hook for validating slug availability with debouncing
 *
 * Features:
 * - Validates slug format (lowercase, alphanumeric + hyphens)
 * - Checks minimum length
 * - Debounces API calls to reduce server load
 * - Provides real-time feedback
 */
export function useSlugValidation(options: UseSlugValidationOptions = {}) {
  const { minLength = 3, debounceMs = 500, onStatusChange } = options;

  const [status, setStatus] = useState<SlugStatus>('idle');
  const [slug, setSlug] = useState('');
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Notify parent when status changes
  useEffect(() => {
    onStatusChange?.(status);
  }, [status, onStatusChange]);

  /**
   * Normalize slug: lowercase, only allowed characters, no leading/trailing hyphens
   */
  const normalizeSlug = useCallback((value: string): string => {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-') // Replace invalid chars with hyphens
      .replace(/-+/g, '-')          // Collapse multiple hyphens
      .replace(/^-|-$/g, '');       // Remove leading/trailing hyphens
  }, []);

  /**
   * Validate slug format
   * Returns true if format is valid, false otherwise
   */
  const isValidFormat = useCallback(
    (value: string): boolean => {
      if (!value || value.length < minLength) {
        return false;
      }

      // Must only contain lowercase letters, numbers, and hyphens
      if (!/^[a-z0-9-]+$/.test(value)) {
        return false;
      }

      // Must not start or end with hyphen
      if (value.startsWith('-') || value.endsWith('-')) {
        return false;
      }

      return true;
    },
    [minLength]
  );

  /**
   * Check slug availability via API
   */
  const checkAvailability = useCallback(async (slugToCheck: string) => {
    try {
      const result = await checkSlugAvailability(slugToCheck);
      setStatus(result.available ? 'available' : 'taken');
    } catch (error) {
      console.error('Error checking slug availability:', error);
      // On error, assume it might be available and let server validate on submit
      setStatus('available');
    }
  }, []);

  /**
   * Validate and check a slug value
   * This is the main function to call when slug changes
   */
  const validateSlug = useCallback(
    (value: string) => {
      // Clear any pending check
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      const normalized = normalizeSlug(value);
      setSlug(normalized);

      // If empty, reset to idle
      if (!normalized) {
        setStatus('idle');
        return normalized;
      }

      // Check format validity
      if (!isValidFormat(normalized)) {
        setStatus('invalid');
        return normalized;
      }

      // Format is valid, check availability with debounce
      setStatus('checking');

      timeoutRef.current = setTimeout(() => {
        checkAvailability(normalized);
      }, debounceMs);

      return normalized;
    },
    [normalizeSlug, isValidFormat, checkAvailability, debounceMs]
  );

  /**
   * Reset validation state
   */
  const reset = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setSlug('');
    setStatus('idle');
  }, []);

  /**
   * Get display text and style for current status
   */
  const getStatusDisplay = useCallback((): SlugValidationResult & { className: string } => {
    switch (status) {
      case 'checking':
        return {
          status,
          message: 'Vérification...',
          className: 'text-gray-500',
        };
      case 'available':
        return {
          status,
          message: 'Disponible',
          className: 'text-green-600',
        };
      case 'taken':
        return {
          status,
          message: 'Cette adresse est déjà utilisée',
          className: 'text-red-600',
        };
      case 'invalid':
        return {
          status,
          message: `Adresse invalide (min. ${minLength} caractères)`,
          className: 'text-red-600',
        };
      default:
        return {
          status,
          message: undefined,
          className: '',
        };
    }
  }, [status, minLength]);

  return {
    /** Current normalized slug value */
    slug,
    /** Current validation status */
    status,
    /** Whether the slug is valid and available */
    isValid: status === 'available',
    /** Validate a new slug value (normalizes and checks availability) */
    validateSlug,
    /** Set status directly (for external control) */
    setStatus,
    /** Reset validation state */
    reset,
    /** Get display info for current status */
    getStatusDisplay,
    /** Normalize a slug value without validation */
    normalizeSlug,
  };
}
